import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  ArrowLeft, Save, Plus, Trash2, ChevronUp, ChevronDown, Send,
  RefreshCw, ClipboardList, Mail, ExternalLink,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ParentSurveyAdminView({ token, onBack }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState("edit");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [responses, setResponses] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [blastInfo, setBlastInfo] = useState(null);
  const [lastBlast, setLastBlast] = useState(null);
  const [blasting, setBlasting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const fetchSummary = (force = false) => {
    setSummaryLoading(true);
    axios.get(`${API}/admin/parent-survey/summary${force ? "?force=true" : ""}`, { headers, timeout: 120000 })
      .then(res => setSummary(res.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to build AI summary"))
      .finally(() => setSummaryLoading(false));
  };

  useEffect(() => {
    axios.get(`${API}/admin/parent-survey/config`, { headers })
      .then(res => setConfig(res.data))
      .catch(() => toast.error("Failed to load survey config"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === "responses") {
      axios.get(`${API}/admin/parent-survey/responses`, { headers })
        .then(res => setResponses(res.data?.responses || []))
        .catch(() => toast.error("Failed to load responses"));
    }
    if (tab === "blast") {
      axios.post(`${API}/admin/parent-survey/blast`, {}, { headers })
        .then(res => setBlastInfo(res.data)).catch(() => {});
      fetchBlastStatus();
    }
    if (tab === "summary" && !summary) fetchSummary();
  }, [tab]);

  const fetchBlastStatus = () => {
    axios.get(`${API}/admin/parent-survey/blast/status`, { headers })
      .then(res => setLastBlast(res.data?.last_blast || null)).catch(() => {});
  };

  useEffect(() => {
    if (tab !== "blast" || lastBlast?.status !== "running") return;
    const t = setInterval(fetchBlastStatus, 5000);
    return () => clearInterval(t);
  }, [tab, lastBlast?.status]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/parent-survey/config`, config, { headers });
      toast.success("Survey saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updQ = (idx, patch) => {
    setConfig(prev => {
      const qs = [...prev.questions];
      qs[idx] = { ...qs[idx], ...patch };
      return { ...prev, questions: qs };
    });
  };
  const moveQ = (idx, dir) => {
    setConfig(prev => {
      const qs = [...prev.questions];
      const j = idx + dir;
      if (j < 0 || j >= qs.length) return prev;
      [qs[idx], qs[j]] = [qs[j], qs[idx]];
      return { ...prev, questions: qs };
    });
  };
  const delQ = (idx) => setConfig(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
  const addQ = () => setConfig(prev => ({
    ...prev,
    questions: [...prev.questions, { id: `q-${Date.now()}`, type: "text", required: false, label: "New question", help: "", options: [] }],
  }));

  const sendBlast = async () => {
    if (!window.confirm(`Send the survey email to ${blastInfo?.recipient_count || "all"} parent emails? This cannot be undone.`)) return;
    setBlasting(true);
    try {
      const res = await axios.post(`${API}/admin/parent-survey/blast`, { confirm: true }, { headers });
      toast.success(`Blast started — sending to ${res.data.total} parents`);
      fetchBlastStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Blast failed to start");
    } finally {
      setBlasting(false);
    }
  };

  if (loading || !config) {
    return <div className="py-16 text-center text-gray-400">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="parent-survey-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="parent-survey-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h2 className="font-bold text-lg flex items-center gap-2 text-gray-900">
            <ClipboardList className="h-5 w-5 text-cyan-600" /> Parent Survey
          </h2>
        </div>
        <a href="/parentsurvey" target="_blank" rel="noreferrer"
          className="text-xs font-semibold text-cyan-700 flex items-center gap-1 hover:underline"
          data-testid="parent-survey-preview-link">
          <ExternalLink className="h-3.5 w-3.5" /> Preview /parentsurvey
        </a>
      </div>

      <div className="flex gap-2 border-b">
        {[["edit", "Edit Survey"], ["responses", `Responses`], ["summary", "AI Summary"], ["blast", "Email Blast"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-cyan-600 text-cyan-700" : "border-transparent text-gray-500"}`}
            data-testid={`parent-survey-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "edit" && (
        <div className="space-y-4">
          <Card><CardContent className="p-4 space-y-3">
            <div>
              <label className="text-xs font-bold text-gray-600">Survey Title</label>
              <Input value={config.title || ""} onChange={e => setConfig({ ...config, title: e.target.value })} className="mt-1" data-testid="parent-survey-title-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600">Intro Text</label>
              <Textarea value={config.intro || ""} onChange={e => setConfig({ ...config, intro: e.target.value })} rows={4} className="mt-1" data-testid="parent-survey-intro-input" />
            </div>
          </CardContent></Card>

          {config.questions.map((q, idx) => (
            <Card key={q.id} className="border-gray-200" data-testid={`parent-survey-q-editor-${idx}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">#{idx + 1}</Badge>
                    <select value={q.type} onChange={e => updQ(idx, { type: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white">
                      <option value="text">Text answer</option>
                      <option value="select">Multiple choice</option>
                      <option value="info">Info block (no answer)</option>
                    </select>
                    {q.type !== "info" && (
                      <label className="flex items-center gap-1 text-xs text-gray-600">
                        <input type="checkbox" checked={!!q.required} onChange={e => updQ(idx, { required: e.target.checked })} /> Required
                      </label>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveQ(idx, -1)} className="text-gray-400 hover:text-gray-700"><ChevronUp className="h-4 w-4" /></button>
                    <button onClick={() => moveQ(idx, 1)} className="text-gray-400 hover:text-gray-700"><ChevronDown className="h-4 w-4" /></button>
                    <button onClick={() => delQ(idx)} className="text-gray-400 hover:text-red-600" data-testid={`parent-survey-q-delete-${idx}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <Textarea value={q.label} onChange={e => updQ(idx, { label: e.target.value })} rows={2} className="text-sm" placeholder="Question text" />
                {q.type !== "info" && (
                  <Input value={q.help || ""} onChange={e => updQ(idx, { help: e.target.value })} placeholder="Help text (optional, shown in italics)" className="text-xs" />
                )}
                {q.type === "info" && (
                  <Textarea value={q.help || ""} onChange={e => updQ(idx, { help: e.target.value })} rows={4} className="text-xs" placeholder="Info body" />
                )}
                {q.type === "select" && (
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase">Options (one per line)</label>
                    <Textarea
                      value={(q.options || []).join("\n")}
                      onChange={e => updQ(idx, { options: e.target.value.split("\n") })}
                      onBlur={e => updQ(idx, { options: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
                      rows={Math.min(9, (q.options || []).length + 1)}
                      className="text-xs mt-1"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" onClick={addQ} className="w-full border-dashed" data-testid="parent-survey-add-q">
            <Plus className="h-4 w-4 mr-1" /> Add Question
          </Button>

          <Card className="border-emerald-200"><CardContent className="p-4 space-y-3">
            <p className="text-sm font-bold text-emerald-800">Thank-You Screen (after submit)</p>
            <Input value={config.thank_you?.heading || ""} onChange={e => setConfig({ ...config, thank_you: { ...config.thank_you, heading: e.target.value } })} placeholder="Heading" />
            <Textarea value={config.thank_you?.message || ""} onChange={e => setConfig({ ...config, thank_you: { ...config.thank_you, message: e.target.value } })} rows={2} placeholder="Message (e.g. Please review us on Yelp or Google)" />
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase">Google review link</label>
              <Input value={config.thank_you?.google_url || ""} onChange={e => setConfig({ ...config, thank_you: { ...config.thank_you, google_url: e.target.value } })} className="text-xs" data-testid="parent-survey-google-url" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase">Yelp review link</label>
              <Input value={config.thank_you?.yelp_url || ""} onChange={e => setConfig({ ...config, thank_you: { ...config.thank_you, yelp_url: e.target.value } })} className="text-xs" data-testid="parent-survey-yelp-url" />
            </div>
            <Textarea value={config.thank_you?.screenshot_note || ""} onChange={e => setConfig({ ...config, thank_you: { ...config.thank_you, screenshot_note: e.target.value } })} rows={2} placeholder="Screenshot / discount note" />
          </CardContent></Card>

          <Card className="border-blue-200"><CardContent className="p-4 space-y-3">
            <p className="text-sm font-bold text-[#1E3A8A] flex items-center gap-1.5"><Mail className="h-4 w-4" /> Blast Email Content</p>
            <Input value={config.email?.subject || ""} onChange={e => setConfig({ ...config, email: { ...config.email, subject: e.target.value } })} placeholder="Email subject" data-testid="parent-survey-email-subject" />
            <Textarea value={config.email?.message || ""} onChange={e => setConfig({ ...config, email: { ...config.email, message: e.target.value } })} rows={3} placeholder="Email message" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Survey link in email</label>
                <Input value={config.email?.survey_link || ""} onChange={e => setConfig({ ...config, email: { ...config.email, survey_link: e.target.value } })} className="text-xs" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Button text</label>
                <Input value={config.email?.button_text || ""} onChange={e => setConfig({ ...config, email: { ...config.email, button_text: e.target.value } })} className="text-xs" />
              </div>
            </div>
          </CardContent></Card>

          <div className="sticky bottom-3">
            <Button onClick={save} disabled={saving} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold shadow-lg" data-testid="parent-survey-save-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save Survey"}
            </Button>
          </div>
        </div>
      )}

      {tab === "responses" && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600 font-semibold">{responses.length} response{responses.length === 1 ? "" : "s"}</p>
          {responses.length === 0 && <p className="text-sm text-gray-400 italic py-8 text-center">No responses yet.</p>}
          {responses.map((r) => {
            const isOpen = expanded === r.id;
            const division = (r.answers || []).find(a => a.id === "q1")?.answer;
            return (
              <Card key={r.id} className="border-gray-200" data-testid={`parent-survey-response-${r.id}`}>
                <button className="w-full text-left" onClick={() => setExpanded(isOpen ? null : r.id)}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="text-xs">
                      <span className="font-bold text-gray-900">
                        {new Date(r.submitted_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </span>
                      {division && <Badge variant="outline" className="ml-2 text-[10px]">{division}</Badge>}
                    </div>
                    {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </CardContent>
                </button>
                {isOpen && (
                  <CardContent className="px-3 pb-3 pt-0 space-y-2 border-t">
                    {(r.answers || []).map((a, i) => (
                      <div key={i} className="text-xs">
                        <p className="font-semibold text-gray-700">{a.label}</p>
                        <p className="text-gray-600 whitespace-pre-line">{a.answer || <span className="text-gray-300">—</span>}</p>
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {tab === "summary" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-gray-500">
              {summary?.generated_at
                ? <>Built from <b>{summary.response_count}</b> response{summary.response_count === 1 ? "" : "s"} · updated {new Date(summary.generated_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · auto-refreshes when new surveys come in</>
                : "AI summary of all survey responses, broken down by category."}
            </p>
            <Button variant="outline" size="sm" onClick={() => fetchSummary(true)} disabled={summaryLoading} data-testid="parent-survey-summary-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${summaryLoading ? "animate-spin" : ""}`} /> Rebuild
            </Button>
          </div>

          {summaryLoading && (
            <Card className="border-cyan-200"><CardContent className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-3"></div>
              <p className="text-sm text-gray-600 font-medium">Gemini is reading the surveys…</p>
              <p className="text-xs text-gray-400 mt-1">This can take up to a minute with many responses.</p>
            </CardContent></Card>
          )}

          {!summaryLoading && summary && summary.response_count === 0 && (
            <p className="text-sm text-gray-400 italic py-8 text-center">No responses yet — the summary will appear once surveys come in.</p>
          )}

          {!summaryLoading && summary && (summary.stats || []).map((s, i) => (
            <Card key={`stat-${i}`} className="border-blue-200 bg-blue-50/40" data-testid={`parent-survey-stat-block-${i}`}>
              <CardContent className="p-4">
                <p className="text-sm font-bold text-[#1E3A8A] mb-2">{s.question}</p>
                <div className="space-y-1">
                  {(s.counts || []).map(([opt, n]) => (
                    <div key={opt} className="flex items-center justify-between text-xs">
                      <span className="text-gray-700">{opt}</span>
                      <Badge variant="outline" className="text-[10px] font-bold">{n}</Badge>
                    </div>
                  ))}
                  {(s.counts || []).length === 0 && <p className="text-xs text-gray-400 italic">No answers yet</p>}
                </div>
              </CardContent>
            </Card>
          ))}

          {!summaryLoading && summary && (summary.blocks || []).map((b, i) => (
            <Card key={`blk-${i}`} className="border-cyan-200" data-testid={`parent-survey-ai-block-${i}`}>
              <CardContent className="p-4">
                <p className="text-sm font-bold text-cyan-800 mb-1.5">{b.category}</p>
                <p className="text-sm text-gray-700">{b.summary}</p>
                {(b.highlights || []).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {b.highlights.map((h, j) => (
                      <li key={j} className="text-xs text-gray-600 flex gap-1.5">
                        <span className="text-cyan-500 font-bold">•</span> {h}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tab === "blast" && (
        <div className="space-y-4 max-w-lg">
          <Card className="border-blue-200"><CardContent className="p-4 space-y-3">
            <p className="text-sm font-bold text-[#1E3A8A]">End-of-Camp Email Blast</p>
            <p className="text-xs text-gray-600">
              Sends the survey email (subject: <span className="font-semibold">"{config.email?.subject}"</span>) with a
              link to <span className="font-mono">{config.email?.survey_link}</span> to every parent email on file
              (primary + secondary, deduplicated).
            </p>
            <p className="text-sm font-semibold" data-testid="parent-survey-blast-count">
              Recipients: {blastInfo ? blastInfo.recipient_count : "…"}
            </p>
            <Button onClick={sendBlast} disabled={blasting || lastBlast?.status === "running"}
              className="w-full bg-[#1E3A8A] hover:bg-blue-800 text-white font-bold" data-testid="parent-survey-blast-btn">
              <Send className="h-4 w-4 mr-1" />
              {lastBlast?.status === "running" ? "Blast in progress…" : blasting ? "Starting…" : "Send Survey to All Parents"}
            </Button>
          </CardContent></Card>

          {lastBlast && (
            <Card className="border-gray-200"><CardContent className="p-4 space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-gray-600">Last Blast</p>
                <button onClick={fetchBlastStatus} className="text-gray-400 hover:text-gray-700"><RefreshCw className="h-3.5 w-3.5" /></button>
              </div>
              <p className="text-xs text-gray-600">
                Status: <Badge className={lastBlast.status === "done" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{lastBlast.status}</Badge>
                <span className="ml-2">Sent {lastBlast.sent}/{lastBlast.total}{lastBlast.failed ? ` · ${lastBlast.failed} failed` : ""}</span>
              </p>
              <p className="text-[10px] text-gray-400">
                Started {new Date(lastBlast.started_at).toLocaleString("en-US", { timeZone: "America/New_York" })} by {lastBlast.started_by}
              </p>
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}
